import { NavLink, useLocation } from "react-router-dom";
import { SIDEBAR } from "../data/sidebarConfig";
import * as Icons from "lucide-react";
import { useState, useMemo } from "react";
import { ChevronDown, Settings, LogOut, User as UserIcon } from "lucide-react";
import { useAuth } from "../lib/authContext";
import { useTheme } from "../lib/themeContext";
import CDOLogo from "./CDOLogo";

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} strokeWidth={1.6} />;
};

const BADGE_COLORS = {
  NEW: "bg-green-100 text-green-700 border-green-200",
  AI: "bg-purple-100 text-purple-700 border-purple-200",
  PRO: "bg-orange-100 text-orange-700 border-orange-200",
  AUTH: "bg-blue-100 text-blue-700 border-blue-200",
  OAUTH: "bg-pink-100 text-pink-700 border-pink-200",
  LAB: "bg-violet-100 text-violet-700 border-violet-200",
  QUIZ: "bg-yellow-100 text-yellow-700 border-yellow-200",
};

export default function Sidebar() {
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { sidebarSize } = useTheme() || { sidebarSize: "normal" };

  const sections = useMemo(() => {
    if (isAdmin) return SIDEBAR;
    return SIDEBAR
      .map(s => ({ ...s, items: s.items.filter(i => i.userAccessible) }))
      .filter(s => s.items.length > 0);
  }, [isAdmin]);

  const [open, setOpen] = useState(() =>
    Object.fromEntries(sections.map(s => [s.id, true]))
  );

  const width = sidebarSize === "compact" ? "w-[220px]" : sidebarSize === "large" ? "w-[300px]" : "w-[260px]";

  return (
    <aside
      className={`${width} shrink-0 h-screen sticky top-0 flex flex-col text-white`}
      style={{ background: "var(--bg-sidebar)" }}
      data-testid="sidebar"
    >
      <div className="px-5 py-5 flex items-center gap-3" style={{ borderBottom: "1px solid var(--border-sidebar)" }}>
        <CDOLogo size={36} withText={true} />
      </div>

      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4 space-y-4">
        {sections.map(section => (
          <div key={section.id}>
            <button
              onClick={() => setOpen(o => ({ ...o, [section.id]: !o[section.id] }))}
              className="w-full flex items-center justify-between px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] transition hover:opacity-100"
              style={{ color: "var(--text-on-sidebar-muted)" }}
              data-testid={`sidebar-section-${section.id}`}
            >
              <span className="flex items-center gap-2">
                {section.color && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: section.color }} />}
                {section.label}
              </span>
              <ChevronDown className={`w-3 h-3 transition-transform ${open[section.id] ? "" : "-rotate-90"}`} />
            </button>
            {open[section.id] && (
              <div className="mt-1 space-y-0.5">
                {section.items.map(item => {
                  const active = location.pathname === item.path;
                  return (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      data-testid={`sidebar-tab-${item.id}`}
                      className="flex items-center gap-3 px-2.5 py-2 text-[13px] rounded-lg transition-all"
                      style={{
                        backgroundColor: active ? "var(--bg-sidebar-item-active)" : "transparent",
                        color: active ? "#FFFFFF" : "var(--text-on-sidebar)",
                        fontWeight: active ? 600 : 500,
                      }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = "var(--bg-sidebar-item-hover)"; }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = "transparent"; }}
                    >
                      <Icon name={item.icon} className="w-[15px] h-[15px] shrink-0" />
                      <span className="truncate flex-1">{item.label}</span>
                      {item.badge && (
                        <span className={`text-[9px] font-mono font-semibold uppercase tracking-wider px-1.5 py-px rounded-md border ${BADGE_COLORS[item.badge] || "bg-white/10 text-white border-white/20"} shrink-0`}>
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* User profile + settings */}
      <div className="px-3 py-3" style={{ borderTop: "1px solid var(--border-sidebar)" }}>
        <NavLink
          to="/settings"
          data-testid="sidebar-settings"
          className="flex items-center gap-3 px-2.5 py-2 text-[13px] rounded-lg transition mb-2"
          style={{ color: "var(--text-on-sidebar)" }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = "var(--bg-sidebar-item-hover)"}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}
        >
          <Settings className="w-[15px] h-[15px] shrink-0" strokeWidth={1.6} />
          <span>Paramètres</span>
        </NavLink>

        {user && (
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div className="w-8 h-8 rounded-full cdo-gradient flex items-center justify-center text-white text-xs font-bold shrink-0">
              {(user.name || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold truncate" style={{ color: "#FFFFFF" }}>{user.name}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider truncate" style={{ color: "var(--text-on-sidebar-muted)" }}>{user.role}</div>
            </div>
            <button onClick={logout} data-testid="logout-btn" title="Déconnexion" className="text-white/40 hover:text-white p-1">
              <LogOut className="w-4 h-4" strokeWidth={1.6} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
